import { Octokit } from '@octokit/rest';

import {
  TechRadarApi,
  techRadarApiRef,
} from '@backstage-community/plugin-tech-radar';
import { TechRadarLoaderResponse } from '@backstage-community/plugin-tech-radar-common';
import { CatalogApi } from '@backstage/catalog-client';
import { createApiFactory } from '@backstage/core-plugin-api';
import { ScmAuthApi, scmAuthApiRef } from '@backstage/integration-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

export type PlatformCapability = {
  name: string;
  tt_capability?: string;
  dora_capability?: string;
  status: 'Adopt' | 'Hold' | 'Assess' | 'Trial';
  documentation_url?: string;
  supporting_team_id?: string;
  supporting_team_title?: string;
  slack_channel?: string;
  slack_link?: string;
  date?: string;
};
export class TechRadarClient implements TechRadarApi {
  private readonly PE_CAPABILITIES_HOST = 'github.com';
  private readonly PE_CAPABILITIES_WORKSPACE = 'tomtom-internal';
  private readonly PE_CAPABILITIES_REPOSITORY = 'meta-data';
  private readonly PE_CAPABILITIES_PATH = 'config/pe-capabilities.json';

  constructor(
    private readonly scmAuthApi: ScmAuthApi,
    private readonly catalogApi: CatalogApi,
  ) {}

  async load(): Promise<TechRadarLoaderResponse> {
    const [encodedHost, encodedWorkspace, encodedRepositoryName] = [
      this.PE_CAPABILITIES_HOST,
      this.PE_CAPABILITIES_WORKSPACE,
      this.PE_CAPABILITIES_REPOSITORY,
    ].map(encodeURIComponent);

    let rawData: PlatformCapability[] = [];

    const { token } = await this.scmAuthApi.getCredentials({
      url: `https://${encodedHost}/${encodedWorkspace}/`,
      additionalScope: { customScopes: { github: [] } },
    });

    const octokit = new Octokit({
      auth: token,
    });

    const response = await octokit.repos.getContent({
      owner: encodedWorkspace,
      repo: encodedRepositoryName,
      path: this.PE_CAPABILITIES_PATH,
    });

    if (!Array.isArray(response.data) && 'content' in response.data) {
      rawData = JSON.parse(atob(response.data.content)) ?? [];
    } else {
      rawData = [];
    }

    const allEntities = await this.catalogApi.getEntities({
      filter: { kind: 'Group' },
    });

    const teamIdToTitleMap: Record<string, string> = {};

    allEntities.items.forEach(entity => {
      const annotations = entity.metadata.annotations || {};
      const supOrgId =
        annotations['graph.microsoft.com/supervisory-org-id'] ??
        annotations['workday.com/supervisory-org-id'];

      if (supOrgId) {
        teamIdToTitleMap[supOrgId.trim().toUpperCase()] =
          entity.metadata.title ?? entity.metadata.name;
      }
    });

    const rings: TechRadarLoaderResponse['rings'] = [
      { id: 'adopt', name: 'ADOPT', color: '#4caf50' },
      { id: 'trial', name: 'TRIAL', color: '#2196f3' },
      { id: 'assess', name: 'ASSESS', color: '#ff9800' },
      { id: 'hold', name: 'HOLD', color: '#f44336' },
    ];

    const QUADRANT_POSITION_ORDER = ['Code', 'Collaborate', 'Build', 'Run'];
    const DISPLAY_ORDER = ['Build', 'Run', 'Collaborate', 'Code'];

    const uniqueTtCapabilities = new Set(
      rawData
        .map((cap: PlatformCapability) => cap.tt_capability)
        .filter(Boolean),
    );

    const orderedQuadrants = QUADRANT_POSITION_ORDER.filter(name =>
      uniqueTtCapabilities.has(name),
    ).map(ttCap => ({
      id: ttCap.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      name: ttCap,
    }));

    const extraQuadrants = Array.from(uniqueTtCapabilities)
      .filter(cap => !QUADRANT_POSITION_ORDER.includes(cap as string))
      .map(ttCap => ({
        id: (ttCap as string).toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        name: ttCap as string,
      }));

    const allQuadrants = [...orderedQuadrants, ...extraQuadrants];

    if (allQuadrants.length === 0) {
      allQuadrants.push({
        id: 'misc-capabilities',
        name: 'Miscellaneous Capabilities',
      });
    }

    const sortedRawData = [...rawData].sort((a, b) => {
      const aIndex = DISPLAY_ORDER.indexOf(a.tt_capability ?? '');
      const bIndex = DISPLAY_ORDER.indexOf(b.tt_capability ?? '');
      const aOrder = aIndex === -1 ? DISPLAY_ORDER.length : aIndex;
      const bOrder = bIndex === -1 ? DISPLAY_ORDER.length : bIndex;
      return aOrder - bOrder;
    });

    const entries: TechRadarLoaderResponse['entries'] = sortedRawData.map(
      (capability: PlatformCapability) => {
        let ringId: string;
        switch (capability.status.toLowerCase()) {
          case 'adopt':
            ringId = 'adopt';
            break;
          case 'trial':
            ringId = 'trial';
            break;
          case 'hold':
            ringId = 'hold';
            break;
          case 'assess':
          case 'asses':
            ringId = 'assess';
            break;
          default:
            ringId = 'assess';
            break;
        }

        const quadrantId = capability.tt_capability
          ? capability.tt_capability.toLowerCase().replace(/[^a-z0-9]+/g, '-')
          : 'misc-capabilities';

        const normalizedTeamId = capability.supporting_team_id
          ?.trim()
          .toUpperCase();

        const teamTitle = normalizedTeamId
          ? teamIdToTitleMap[normalizedTeamId] ?? capability.supporting_team_id
          : null;

        const teamIdLower = capability.supporting_team_id?.trim().toLowerCase();

        const MAX_TITLE_LENGTH = 40;
        const title =
          capability.name.length > MAX_TITLE_LENGTH
            ? `${capability.name.substring(0, MAX_TITLE_LENGTH - 3)}...`
            : capability.name;

        const descriptionParts: string[] = [];
        if (capability.tt_capability) {
          descriptionParts.push(
            `**TT Capability:** ${capability.tt_capability}`,
          );
        }
        if (capability.dora_capability) {
          descriptionParts.push(
            `**DORA Capability:** ${capability.dora_capability}`,
          );
        }
        const description = descriptionParts.join('\n\n');

        const links: { url: string; title: string }[] = [];

        if (capability.documentation_url) {
          links.push({
            url: capability.documentation_url,
            title: 'Documentation',
          });
        }
        if (capability.slack_link && capability.slack_channel) {
          links.push({
            url: capability.slack_link,
            title: capability.slack_channel,
          });
        }
        if (teamIdLower) {
          links.push({
            url: `/catalog/default/Group/${teamIdLower}`,
            title: teamTitle ? `Team: ${teamTitle}` : `Team: ${teamIdLower}`,
          });
        }

        const id = capability.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-+)|(-+$)/g, '');

        return {
          key: id,
          id,
          title,
          quadrant: quadrantId,
          description,
          timeline: [
            {
              ringId,
              moved: 0,
              date: capability.date ? new Date(capability.date) : new Date(),
              description: '',
            },
          ],
          links,
        };
      },
    );

    return {
      quadrants: allQuadrants,
      rings,
      entries,
    };
  }
}

export const techRadarClientFactory = createApiFactory({
  api: techRadarApiRef,
  deps: { scmAuthApi: scmAuthApiRef, catalogApi: catalogApiRef },
  factory: ({ scmAuthApi, catalogApi }) =>
    new TechRadarClient(scmAuthApi, catalogApi),
});
